import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputBase,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import SearchIcon from "@mui/icons-material/Search";
import Toast from "../../../utils/toast";
import createInstance from "../../../api/axiosinstance";
import TP_FeedbackForm from "../TP_FeedbackForm";
import { useThemeMode } from "../../Themes/ThemeContextTemp.js";

const TP_Dashboard = () => {
  const [interviews, setInterviews] = useState([]);
  const [filteredInterviews, setFilteredInterviews] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({ interviewId: "" });
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { COLORS } = useThemeMode();

  const aiDishaApi = createInstance("DISHA");

  // ✅ Get current logged-in TP interviewer details
  const { user } = useSelector((state) => state.auth);
  const tpInterviewerId = user?.panelistId;
  const tpName = user?.name || "Guest";
  const tpEmail = user?.email || "guest@placeholder.com";

  // ✅ Fetch upcoming interviews
  const fetchUpcomingInterviews = async () => {
    if (!tpInterviewerId) {
      Toast.error("TP Interviewer ID missing. Please log in again.");
      return;
    }

    setIsLoading(true);
    try {
      const response = await aiDishaApi.get(
        `/TP_Interviewer_upcoming_interviews?interviewer_id=${tpInterviewerId}`
      );

      const data = response?.data?.upcoming_interviews || [];
      setInterviews(data);
      setFilteredInterviews(data);
      console.log("data", response);
    } catch (error) {
      console.error("Error fetching upcoming interviews:", error);
      Toast.error("Failed to load interviews. Please refresh.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchUpcomingInterviews();
  }, [tpInterviewerId]);

  // ✅ Handle Feedback Modal
  const handleFeedbackModalOpen = (interviewIdTemp) => {
    setFormData({ ...formData, interviewId: interviewIdTemp });
    setIsFeedbackModalOpen(true);
  };
  const handleFeedbackModalClose = () => setIsFeedbackModalOpen(false);

  // ✅ Handle Status Change using new PATCH API
  const handleInterviewStatusChange = async (interview, status) => {
    Toast.success(`Updating interview ${interview.interview_id} as ${status}...`);

    try {
      const response = await aiDishaApi.patch(
        `/accept/reject/TpInterviewer`,
        {},
        {
          params: {
            interviewer_id: tpInterviewerId,
            interview_id: interview.interview_id,
            Status: status,
          },
        }
      );

      Toast.success(response?.data?.message || `Interview marked as ${status}`);
      fetchUpcomingInterviews(); // 🔄 Refresh table
    } catch (error) {
      console.error("Error updating interview status:", error);
      Toast.error("Failed to update interview status.");
    }
  };

  // ✅ Format Date/Time
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "N/A";
    const date = new Date(dateTimeString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day} ${month} ${year}, ${hours}:${minutes}`;
  };

  // ✅ Filter table by search
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = interviews.filter(
      (i) =>
        i.candidate_name?.toLowerCase().includes(value) ||
        i.job_title?.toLowerCase().includes(value)
    );
    setFilteredInterviews(filtered);
  };

  const columns = [
    "Interview Slot",
    "Round",
    "Meeting Link",
    "Candidate",
    "Job",
    "Feedback",
    "Status",
    "Action",
  ];

  return (
    <div style={{ backgroundColor: "white", minHeight: "80vh" }}>
      {/* Header */}
      <Box
        display="grid"
        gridTemplateColumns="repeat(12, 1fr)"
        sx={{
          marginTop: "auto",
          alignItems: "center",
          borderRadius: "10px",
          padding: "1% 1%",
        }}
      >
        <Box gridColumn="span 12">
          <Typography
            sx={{ fontFamily: "poppins", fontSize: "28px", color: COLORS.text }}
          >
            TP Interview Dashboard
          </Typography>
        </Box>

        {/* Search Bar */}
        <Box gridColumn="span 12" sx={{ marginTop: "1%", marginBottom: "2%" }}>
          <Paper
            sx={{
              p: "2px 4px",
              display: "flex",
              width: "65%",
              borderRadius: 2,
              border: "1px solid #033538",
              background:COLORS.primary,
            }}
          >
            <IconButton sx={{ p: "8px", color: COLORS.text }}>
              <SearchIcon sx={{ fill: "#033538" }} />
            </IconButton>
            <InputBase
              sx={{
                ml: 1,
                flex: 1,
                fontFamily: "poppins",
                "& .MuiInputBase-input::placeholder": {
                  color: `${COLORS.text} !important`,
                  opacity: 0.7,
                },
              }}
              placeholder="Search by candidate or job title"
              value={searchTerm}
              onChange={handleSearch}
            />
          </Paper>
        </Box>
      </Box>

      {/* Loader */}
      {isLoading ? (
        <Box sx={{ textAlign: "center", marginTop: "80px" }}>
          <CircularProgress sx={{ color: COLORS.text }} />
          <Typography sx={{ color: COLORS.text, fontFamily: "poppins", mt: 2 }}>
            Loading upcoming interviews...
          </Typography>
        </Box>
      ) : filteredInterviews.length === 0 ? (
        <Typography
          sx={{
            textAlign: "center",
            marginTop: "50px",
            color: COLORS.text,
            fontFamily: "poppins",
            fontSize: "18px",
          }}
        >
          No upcoming interviews found.
        </Typography>
      ) : (
        /* Table */
        <Grid container spacing={1} sx={{ justifyContent: "center" }}>
          <Grid item xs={9}>
            <TableContainer
              component={Paper}
              sx={{ marginTop: "25px", borderRadius: "10px" }}
            >
              <Table size="small">
                <TableHead sx={{ background: COLORS.primary }}>
                  <TableRow>
                    {columns.map((col, index) => (
                      <TableCell
                        key={index}
                        sx={{
                          color: COLORS.text,
                          fontSize: "16px",
                          textAlign: "center",
                          fontFamily: "poppins",
                        }}
                      >
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredInterviews.map((interview) => (
                    <TableRow
                      key={interview.interview_id}
                      sx={{
                        background: COLORS.background,
                        "&:hover": { background: COLORS.hover_btn },
                      }}
                    >
                      <TableCell sx={{ color: COLORS.text, textAlign: "center" }}>
                        {formatDateTime(interview.interview_date_and_time)}
                      </TableCell>
                      <TableCell sx={{ color:COLORS.text, textAlign: "center" }}>
                        {interview.round}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.text, textAlign: "center" }}>
                        <a
                          href={interview.meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: COLORS.button_red_bg }}
                        >
                          Join
                        </a>
                      </TableCell>
                      <TableCell sx={{ color: COLORS.text, textAlign: "center" }}>
                        {interview.candidate_name}
                      </TableCell>
                      <TableCell sx={{ color: COLORS.text, textAlign: "center" }}>
                        {interview.job_title}
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Button
                          variant="contained"
                          sx={{
                            background: COLORS.button_edit_bg,
                            color: COLORS.text,
                            fontFamily: "poppins",
                            height: "36px",
                            width: "80px",
                            "&:hover": { background: COLORS.hover_edit_btn },
                          }}
                          onClick={() =>
                            handleFeedbackModalOpen(interview.interview_id)
                          }
                        >
                          Feedback
                        </Button>
                      </TableCell>
                      <TableCell
                        sx={{
                          color: COLORS.text,
                          textAlign: "center",
                        }}
                      >
                        {interview.Interviewer_acceptance || "Pending"}
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Select
                          value=""
                          onChange={(e) =>
                            handleInterviewStatusChange(
                              interview,
                              e.target.value
                            )
                          }
                          displayEmpty
                          sx={{
                            background: COLORS.background,
                            color: COLORS.text,
                            fontFamily: "poppins",
                            height: "36px",
                            "& .MuiSelect-icon": { color: COLORS.text },
                          }}
                        >
                          <MenuItem value="" disabled sx={{ color: "black" }}>
                            Select Action
                          </MenuItem>
                          <MenuItem value="Accept" sx={{ color: "black" }}>
                            Accept
                          </MenuItem>
                          <MenuItem value="Reject" sx={{ color: "black" }}>
                            Reject
                          </MenuItem>
                          <MenuItem value="Re-Schedule" sx={{ color: "black" }}>
                            Re-Schedule
                          </MenuItem>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      )}

      {/* Feedback Modal */}
      <TP_FeedbackForm
        isOpen={isFeedbackModalOpen}
        onClose={handleFeedbackModalClose}
        interviewId={formData.interviewId}
        organizationId={"dummy-org-id"}
      />

      <br />
      <br />
    </div>
  );
};

export default TP_Dashboard;
